/**
 * RedFox Product Customizer
 * Handles tab switching, file uploads, and add to cart integration
 */

class RFCustomizer {
  constructor() {
    this.currentMethod = 'print';
    this.selectedOptions = [];
    this.uploadedFiles = [];
    this.isUploadingFiles = false; // Track upload status
    this.uploadQueue = []; // Queue for pending uploads
    this.isProcessingQueue = false; // Track if queue is being processed
    this.init();
  }

  init() {
    this.setupMethodTabs();
    this.setupFileUpload();
    this.setupAddToCart();
    this.setupOptionTracking();
    this.setupRequiredFieldListeners();
    this.bindThemeEditorEvents();
    this.setupUploadPlaceholderStyling();
    this.syncOneTimeFeeOptionAvailability();

    // Re-check when user returns focus to the page/cart state changes
    window.addEventListener('pageshow', () => this.syncOneTimeFeeOptionAvailability());
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.syncOneTimeFeeOptionAvailability();
    });
    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-cart-notification-item-remove]');
      if (removeButton) {
        setTimeout(() => this.syncOneTimeFeeOptionAvailability(), 700);
      }
    });

    // Re-sync when cart changes (product removed, quantity changed, etc.)
    document.addEventListener('rf:cart:updated', () => {
      setTimeout(() => this.syncOneTimeFeeOptionAvailability(), 300);
    });

    // Fallback: observe cart drawer DOM changes
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification) {
      new MutationObserver(() => {
        setTimeout(() => this.syncOneTimeFeeOptionAvailability(), 500);
      }).observe(cartNotification, { childList: true, subtree: true });
    }
  }

  /**
   * Setup upload placeholder styling observer
   */
  setupUploadPlaceholderStyling() {
    // Style existing placeholders on page load
    this.styleUploadPlaceholders();

    // Watch for cart updates and re-style placeholders
    const observer = new MutationObserver(() => {
      this.styleUploadPlaceholders();
    });

    // Observe cart notification content changes
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification) {
      observer.observe(cartNotification, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Style upload placeholder text with CSS classes
   */
  styleUploadPlaceholders() {
    // Find all product option properties
    const propertyElements = document.querySelectorAll('.product-option-property .product-option span:last-child');

    propertyElements.forEach(span => {
      const text = span.textContent;

      // Check if this is an upload status or uploading file
      if (text.includes('⏳ Uploading') && text.includes('file')) {
        // Upload status badge (e.g., "⏳ Uploading 3 files...")
        if (!span.classList.contains('rf-upload-status')) {
          span.className = 'rf-upload-status';
        }
      } else if (text.includes('🔄 Uploading')) {
        // Individual file upload (e.g., "🔄 Uploading image.png...")
        if (!span.classList.contains('rf-uploading-file')) {
          span.className = 'rf-uploading-file';
        }
      }
    });
  }

  /**
   * Setup method tab switching (Print / Embroidery / etc.)
   */
  setupMethodTabs() {
    const tabs = document.querySelectorAll('.rf-method-tab');
    const methodInput = document.querySelector('.rf-method-input');

    tabs.forEach(tab => {
      if (tab.dataset.rfMethodTabBound === 'true') return;
      tab.dataset.rfMethodTabBound = 'true';

      tab.addEventListener('click', () => {
        const method = tab.dataset.method;
        this.switchMethod(method);

        // Update active tab
        tabs.forEach(t => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
        });
        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');

        // Update hidden input
        if (methodInput) {
          methodInput.value = method.charAt(0).toUpperCase() + method.slice(1);
        }
      });
    });

    // Set initial method and initialize visibility
    const activeTab = document.querySelector('.rf-method-tab.active');
    if (activeTab) {
      this.currentMethod = activeTab.dataset.method;
    }

    // Always initialize visibility.
    // This is required even when no method tabs exist, otherwise
    // blocks with data-method="print/embroidery/..." stay hidden by default CSS.
    this.switchMethod(this.currentMethod);
  }

  /**
   * Show/hide blocks based on selected method
   */
  switchMethod(method) {
    this.currentMethod = method;

    // Show/hide option blocks - use specific selectors for RF blocks only
    const optionBlocks = document.querySelectorAll('.rf-position-option[data-method], .rf-text-input[data-method], .rf-file-upload[data-method], .rf-heading-block[data-method]');

    optionBlocks.forEach(block => {
      const blockMethod = block.dataset.method;
      const hasInlineAdditionalFields =
        block.classList.contains('rf-position-option') && !!block.querySelector('.rf-option-additional-fields');

      if (blockMethod === 'all' || blockMethod === method) {
        block.style.display = hasInlineAdditionalFields ? 'contents' : 'block';
        block.style.visibility = 'visible';
        block.style.opacity = '1';
      } else {
        block.style.display = 'none';
        block.style.visibility = 'hidden';
        block.style.opacity = '0';
      }
    });

    // Clear selections from hidden blocks (skip pre-selected)
    const hiddenInputs = document.querySelectorAll('.rf-position-option[data-method]:not([data-method="all"]):not([data-method="' + method + '"]) input[type="radio"], .rf-position-option[data-method]:not([data-method="all"]):not([data-method="' + method + '"]) input[type="checkbox"]');
    hiddenInputs.forEach(input => {
      if (input.dataset.preSelected === 'true') return;
      input.checked = false;
    });

    // Update selected options
    this.updateSelectedOptions();

    // Clear validation state for groups that are not active for current method
    document.querySelectorAll('.rf-options-grid[data-rf-required="true"]').forEach(group => {
      const visibleOnly = group.dataset.rfRequiredVisibleOnly !== 'false';
      if (visibleOnly && !this.isGroupVisibleForCurrentMethod(group)) {
        this.clearGroupRequiredError(group);
      }
    });
  }

  /**
   * Track selected options (checkboxes/radios)
   */
  setupOptionTracking() {
    const optionInputs = document.querySelectorAll('.rf-option-input');

    optionInputs.forEach(input => {
      if (input.dataset.rfOptionTrackingBound === 'true') return;
      input.dataset.rfOptionTrackingBound = 'true';

      input.addEventListener('change', () => {
        this.updateSelectedOptions();
        this.clearRequiredErrorForInput(input);
      });
    });
  }

  /**
   * Setup listeners to clear required-field errors on user interaction
   */
  setupRequiredFieldListeners() {
    // Clear error on typing in option text fields
    document.querySelectorAll('.rf-option-text-field[required]').forEach(field => {
      if (field.dataset.rfRequiredListenerBound === 'true') return;
      field.dataset.rfRequiredListenerBound = 'true';
      field.addEventListener('input', () => {
        if (field.value && field.value.trim() !== '') {
          const container = field.closest('.rf-option-text-input');
          if (container) {
            container.classList.remove('rf-field--invalid');
            const errorEl = container.querySelector('.rf-field-required-error');
            if (errorEl) errorEl.style.display = 'none';
          }
        }
      });
    });

    // Clear error on typing in standalone text inputs
    document.querySelectorAll('[data-rf-input="text"][required]').forEach(field => {
      if (field.dataset.rfRequiredListenerBound === 'true') return;
      field.dataset.rfRequiredListenerBound = 'true';
      field.addEventListener('input', () => {
        if (field.value && field.value.trim() !== '') {
          const container = field.closest('.rf-text-input');
          if (container) {
            container.classList.remove('rf-field--invalid');
            const errorEl = container.querySelector('.rf-field-required-error');
            if (errorEl) errorEl.style.display = 'none';
          }
        }
      });
    });

    // Clear error on file upload (option files) — use MutationObserver on preview containers
    document.querySelectorAll('.rf-option-file-input[required]').forEach(fileInput => {
      const blockId = fileInput.dataset.rfOptionFile;
      const fileContainer = fileInput.closest('.rf-option-file-upload');
      const previewEl = fileContainer?.querySelector('[data-rf-option-preview]');
      if (previewEl && fileContainer && !previewEl.dataset.rfRequiredObserverBound) {
        previewEl.dataset.rfRequiredObserverBound = 'true';
        new MutationObserver(() => {
          const hasFiles = window.rfOptionFiles && window.rfOptionFiles[blockId] && window.rfOptionFiles[blockId].length > 0;
          if (hasFiles) {
            fileContainer.classList.remove('rf-field--invalid');
            const errorEl = fileContainer.querySelector('.rf-field-required-error');
            if (errorEl) errorEl.style.display = 'none';
          }
        }).observe(previewEl, { childList: true });
      }
    });

    // Clear error on file upload (standalone) — observe preview containers
    document.querySelectorAll('.rf-file-upload').forEach(container => {
      const fileInput = container.querySelector('[data-rf-file-input][required]');
      const previewEl = container.querySelector('[data-rf-preview]');
      if (fileInput && previewEl && !previewEl.dataset.rfRequiredObserverBound) {
        previewEl.dataset.rfRequiredObserverBound = 'true';
        new MutationObserver(() => {
          const hasFiles = previewEl.querySelectorAll('.rf-file-item').length > 0;
          if (hasFiles) {
            container.classList.remove('rf-field--invalid');
            const errorEl = container.querySelector('.rf-field-required-error');
            if (errorEl) errorEl.style.display = 'none';
          }
        }).observe(previewEl, { childList: true });
      }
    });
  }

  isGroupVisibleForCurrentMethod(group) {
    if (!group) return false;

    const groupMethod = group.dataset.method;
    const methodVisible = !groupMethod || groupMethod === 'all' || groupMethod === this.currentMethod;
    if (!methodVisible) return false;

    const style = window.getComputedStyle(group);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  getVisibleSelectableInputs(group) {
    if (!group) return [];

    return Array.from(group.querySelectorAll('.rf-option-input')).filter(input => {
      if (input.disabled) return false;
      if (input.dataset.oneTimeFee === 'true') return false;

      const block = input.closest('.rf-position-option');
      if (!block) return false;

      const blockMethod = block.dataset.method;
      return !blockMethod || blockMethod === 'all' || blockMethod === this.currentMethod;
    });
  }

  setGroupRequiredError(group, message) {
    if (!group) return;
    const errorEl = group.querySelector('[data-rf-required-error]');
    if (errorEl) {
      errorEl.textContent = message || 'Please select at least one option.';
      errorEl.style.display = 'block';
    }
    group.classList.add('rf-options-grid--invalid');
  }

  clearGroupRequiredError(group) {
    if (!group) return;
    const errorEl = group.querySelector('[data-rf-required-error]');
    if (errorEl) errorEl.style.display = 'none';
    group.classList.remove('rf-options-grid--invalid');
  }

  clearRequiredErrorForInput(input) {
    const group = input?.closest('.rf-options-grid');
    if (!group || group.dataset.rfRequired !== 'true') return;

    const visibleInputs = this.getVisibleSelectableInputs(group);
    const hasSelection = visibleInputs.some(optionInput => optionInput.checked);
    if (hasSelection) this.clearGroupRequiredError(group);
  }

  validateRequiredOptionGroups() {
    const requiredGroups = document.querySelectorAll('.rf-options-grid[data-rf-required="true"]');
    let firstInvalidGroup = null;

    requiredGroups.forEach(group => {
      const visibleOnly = group.dataset.rfRequiredVisibleOnly !== 'false';
      const mustValidate = visibleOnly ? this.isGroupVisibleForCurrentMethod(group) : true;

      if (!mustValidate) {
        this.clearGroupRequiredError(group);
        return;
      }

      const visibleInputs = this.getVisibleSelectableInputs(group);
      if (visibleInputs.length === 0) {
        this.clearGroupRequiredError(group);
        return;
      }

      const hasSelection = visibleInputs.some(input => input.checked);
      if (!hasSelection) {
        this.setGroupRequiredError(group, group.dataset.rfRequiredMessage);
        if (!firstInvalidGroup) firstInvalidGroup = group;
      } else {
        this.clearGroupRequiredError(group);
      }
    });

    if (firstInvalidGroup) {
      firstInvalidGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    return true;
  }

  getMainProductId() {
    return document.querySelector('[data-product-id]')?.dataset.productId ||
           document.querySelector('input[name="product-id"]')?.value ||
           document.querySelector('form[data-product-id]')?.dataset.productId ||
           document.querySelector('product-form')?.dataset.productId ||
           window.ShopifyAnalytics?.meta?.product?.id?.toString() ||
           '';
  }

  async syncOneTimeFeeOptionAvailability() {
    const mainProductId = this.getMainProductId();
    if (!mainProductId) return;

    const feeInputs = document.querySelectorAll('.rf-option-input[data-one-time-fee="true"]');
    if (feeInputs.length === 0) return;

    try {
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      const existingFeeVariantIds = new Set(
        cartData.items
          .filter(item =>
            item.properties?._rf_one_time_fee === 'true' &&
            item.properties?._rf_main_product_id === mainProductId
          )
          .map(item => String(item.variant_id))
      );

      feeInputs.forEach(input => {
        // Never disable or uncheck pre-selected options
        if (input.dataset.preSelected === 'true') return;

        const optionVariantId = String(input.dataset.productId || '');
        const shouldDisable = optionVariantId && existingFeeVariantIds.has(optionVariantId);
        const optionLabel = input.closest('.rf-option-label');
        const cardWrapper = input.closest('.rf-option-card-wrapper');
        const blockId = input.id?.replace('rf-option-', '');
        const additionalFields = blockId ? document.querySelector('[data-option-id="' + blockId + '"]') : null;

        input.disabled = shouldDisable;
        input.setAttribute('aria-disabled', shouldDisable ? 'true' : 'false');

        if (optionLabel) {
          optionLabel.classList.toggle('rf-option-label--disabled', shouldDisable);
          optionLabel.setAttribute('title', shouldDisable ? 'This one-time fee is already in cart for this product' : '');
        }

        // Show/hide "In cart" badge
        const card = input.nextElementSibling;
        const inCartBadge = card?.querySelector('.rf-in-cart-badge');
        if (inCartBadge) {
          inCartBadge.classList.toggle('rf-in-cart-badge--visible', shouldDisable);
        }

        if (shouldDisable) {
          // Show checked state visually
          input.checked = true;
          if (additionalFields) additionalFields.style.display = 'none';
          if (cardWrapper) cardWrapper.classList.remove('rf-active');
        } else {
          // Removed from cart — restore to normal selectable state
          input.disabled = false;
          input.setAttribute('aria-disabled', 'false');
          if (input.checked) {
            input.checked = false;
            input.dispatchEvent(new Event('change'));
            if (additionalFields) additionalFields.style.display = 'none';
            if (cardWrapper) cardWrapper.classList.remove('rf-active');
          }
          if (optionLabel) {
            optionLabel.classList.remove('rf-option-label--disabled');
            optionLabel.setAttribute('title', '');
          }
        }
      });

      this.updateSelectedOptions();
    } catch (error) {
      // Keep UI usable even if availability check fails
    }
  }

  /**
   * Update selected options array
   */
  updateSelectedOptions() {
    this.selectedOptions = [];

    // Get all checked options that are visible
    const checkedInputs = document.querySelectorAll('.rf-option-input:checked');

    checkedInputs.forEach(input => {
      // Skip disabled one-time fees (already in cart) — but not pre-selected
      if (input.disabled && input.dataset.preSelected !== 'true') return;

      const block = input.closest('[data-method]');
      const blockMethod = block?.dataset.method;

      // Only include if block is visible (matches current method or is 'all')
      if (!block || blockMethod === 'all' || blockMethod === this.currentMethod) {
        const productId = input.dataset.productId;
        const productPrice = input.dataset.productPrice;
        const productTitle = input.dataset.productTitle;
        const optionValue = input.value;
        const isOneTimeFee = input.dataset.oneTimeFee === 'true';
        const oneTimeFeeCartTitle = input.dataset.oneTimeFeeCartTitle || optionValue;

        if (productId) {
          this.selectedOptions.push({
            id: productId,
            price: productPrice,
            title: productTitle,
            value: optionValue,
            optionType: input.dataset.rfOption,
            isOneTimeFee: isOneTimeFee,
            isPreSelected: input.dataset.preSelected === 'true',
            oneTimeFeeCartTitle: oneTimeFeeCartTitle
          });
        }
      }
    });

  }

  /**
   * Setup file upload handling
   */
  setupFileUpload() {
    const uploadContainers = document.querySelectorAll('.rf-file-upload');

    uploadContainers.forEach(container => {
      const dropzone = container.querySelector('[data-rf-dropzone]');
      const fileInput = container.querySelector('[data-rf-file-input]');
      const preview = container.querySelector('[data-rf-preview]');
      const fileInputsContainer = container.querySelector('[data-rf-file-inputs]');
      const errorContainer = container.querySelector('[data-rf-upload-error]');
      const maxFiles = parseInt(container.dataset.maxFiles) || 3;
      const acceptedFormats = fileInput.getAttribute('accept') || '';
      let files = [];

      // Helper function to show error message
      const showError = (message) => {
        if (errorContainer) {
          const errorMessage = errorContainer.querySelector('.rf-error-message');
          if (errorMessage) {
            errorMessage.textContent = message;
          }
          errorContainer.style.display = 'flex';

          // Auto hide after 15 seconds
          setTimeout(() => {
            errorContainer.style.display = 'none';
          }, 15000);
        }
      };

      // Helper function to hide error message
      const hideError = () => {
        if (errorContainer) {
          errorContainer.style.display = 'none';
        }
      };

      // Setup close button for error message
      if (errorContainer) {
        const closeButton = errorContainer.querySelector('.rf-error-close');
        if (closeButton) {
          closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            hideError();
          });
        }
      }

      // Click to upload
      dropzone.addEventListener('click', (e) => {
        // Only trigger if not clicking the file input itself
        if (e.target !== fileInput) {
          e.preventDefault();
          e.stopPropagation();
          fileInput.click();
        }
      });

      // Drag & drop
      dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropzone.classList.add('dragging');
      });

      dropzone.addEventListener('dragleave', () => {
        dropzone.classList.remove('dragging');
      });

      dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropzone.classList.remove('dragging');

        const droppedFiles = Array.from(e.dataTransfer.files);
        this.handleFiles(droppedFiles, files, maxFiles, preview, fileInputsContainer, fileInput, acceptedFormats, showError, hideError);
      });

      // File input change
      fileInput.addEventListener('change', (e) => {
        const selectedFiles = Array.from(e.target.files);
        this.handleFiles(selectedFiles, files, maxFiles, preview, fileInputsContainer, fileInput, acceptedFormats, showError, hideError);
      });
    });
  }

  /**
   * Validate file type against accepted formats
   */
  isValidFileType(file, acceptedFormats) {
    if (!acceptedFormats || acceptedFormats.trim() === '') {
      return true;
    }

    const formats = acceptedFormats.split(',').map(f => f.trim().toLowerCase());
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    const isValid = formats.some(format => {
      if (format === 'image/*') {
        return fileType.startsWith('image/');
      } else if (format.startsWith('.')) {
        return fileName.endsWith(format);
      } else if (format.includes('/')) {
        return fileType === format;
      } else if (format.startsWith('*')) {
        // Handle wildcards like *.pdf
        const ext = format.replace('*', '');
        return fileName.endsWith(ext);
      }
      return false;
    });

    return isValid;
  }

  /**
   * Handle file selection
   */
  handleFiles(newFiles, existingFiles, maxFiles, preview, fileInputsContainer, fileInput, acceptedFormats, showError, hideError) {
    // Hide any existing errors
    if (hideError) hideError();

    // Check max files limit
    const remainingSlots = maxFiles - existingFiles.length;
    const filesToAdd = newFiles.slice(0, remainingSlots);

    if (newFiles.length > remainingSlots) {
      if (showError) {
        showError(`Maximum ${maxFiles} files allowed`);
      }
      return;
    }

    let rejectedCount = 0;
    let errorMessages = [];

    filesToAdd.forEach(file => {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        errorMessages.push(`"${file.name}" is too large (max 10MB)`);
        rejectedCount++;
        return;
      }

      // Validate file type - STRICT validation
      const isValid = this.isValidFileType(file, acceptedFormats);
      if (!isValid) {
        errorMessages.push(`"${file.name}" is not an accepted format`);
        rejectedCount++;
        return;
      }

      // Only add if validation passed
      existingFiles.push(file);
      this.renderFilePreview(file, existingFiles, preview, fileInputsContainer);
    });

    // Show errors if any
    if (errorMessages.length > 0 && showError) {
      showError(errorMessages.join('. ') + '. Accepted: ' + acceptedFormats);
    }

    // Clear file input to allow re-selecting same file
    fileInput.value = '';
  }

  /**
   * Render file preview
   */
  renderFilePreview(file, filesArray, preview, fileInputsContainer) {
    const fileIndex = filesArray.indexOf(file);
    const isImage = file.type.startsWith('image/');

    const fileItem = document.createElement('div');
    fileItem.className = 'rf-file-item';
    fileItem.dataset.fileIndex = fileIndex;

    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => {
        fileItem.innerHTML = `
          <img src="${e.target.result}" alt="${file.name}">
          <div class="rf-file-name">${file.name}</div>
          <div class="rf-file-size">${this.formatFileSize(file.size)}</div>
          <button type="button" class="rf-file-remove" data-file-index="${fileIndex}">
            <i class="feather-x"></i>
          </button>
        `;
        this.setupFileRemove(fileItem, filesArray, preview, fileInputsContainer);
      };
      reader.readAsDataURL(file);
    } else {
      fileItem.innerHTML = `
        <div class="rf-file-icon"><i class="feather-file"></i></div>
        <div class="rf-file-name">${file.name}</div>
        <div class="rf-file-size">${this.formatFileSize(file.size)}</div>
        <button type="button" class="rf-file-remove" data-file-index="${fileIndex}">
          <i class="feather-x"></i>
        </button>
      `;
      this.setupFileRemove(fileItem, filesArray, preview, fileInputsContainer);
    }

    preview.appendChild(fileItem);

    // Store file reference for form submission
    this.uploadedFiles.push({
      file: file,
      index: fileIndex
    });
  }

  /**
   * Setup file remove button
   */
  setupFileRemove(fileItem, filesArray, preview, fileInputsContainer) {
    const removeBtn = fileItem.querySelector('.rf-file-remove');
    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const fileIndex = parseInt(removeBtn.dataset.fileIndex);

      // Remove from array
      filesArray.splice(fileIndex, 1);
      this.uploadedFiles = this.uploadedFiles.filter(f => f.index !== fileIndex);

      // Remove from DOM
      fileItem.remove();

      // Update file inputs
      this.updateFileInputs(filesArray, fileInputsContainer);
    });
  }

  /**
   * Update hidden file inputs for form submission
   */
  updateFileInputs(files, container) {
    // This will be populated during form submission
    // Files will be added to FormData directly
  }

  /**
   * Format file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Setup add to cart integration
   */
  setupAddToCart() {
    const productForm = document.querySelector('product-form form');

    if (!productForm) return;

    // Check if RF customization is active
    const hasRFBlocks = document.querySelector('.rf-method-selector, .rf-position-option, .rf-file-upload, .rf-text-input');

    if (!hasRFBlocks) return;

    if (productForm.dataset.rfSubmitBound === 'true') return;
    productForm.dataset.rfSubmitBound = 'true';

    // Remove original submit handler to prevent double add
    productForm.onsubmit = null;

    // Add RF submit handler with highest priority (capture phase)
    productForm.addEventListener('submit', (e) => {
      e.preventDefault();
      e.stopImmediatePropagation(); // Stop all other submit handlers
      this.handleRFAddToCart(productForm);
    }, true); // Use capture phase to intercept before other handlers
  }

  bindThemeEditorEvents() {
    if (!window.Shopify || !window.Shopify.designMode) return;
    if (this.themeEditorEventsBound) return;
    this.themeEditorEventsBound = true;

    const reinit = () => {
      window.requestAnimationFrame(() => {
        this.setupMethodTabs();
        this.setupFileUpload();
        this.setupAddToCart();
        this.setupOptionTracking();
        this.switchMethod(this.currentMethod);
        this.syncOneTimeFeeOptionAvailability();
      });
    };

    document.addEventListener('shopify:section:load', reinit);
    document.addEventListener('shopify:section:select', reinit);
    document.addEventListener('shopify:section:reorder', reinit);
    document.addEventListener('shopify:block:select', reinit);
  }

  /**
   * Upload file to Shopify CDN or convert to data URL
   * TODO: Implement actual file upload endpoint (Shopify app or external service)
   */
  async uploadFileToShopify(file) {
    // For small images, use data URL (embedded in cart properties)
    // For larger files, should use external upload service
    if (file.size <= 1 * 1024 * 1024) { // 1MB or less
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }

    // For larger files, try external upload endpoint
    try {
      const formData = new FormData();
      formData.append('file', file);

      // TODO: Replace with actual upload endpoint
      // This could be a Shopify app endpoint, AWS S3, Cloudinary, etc.
      const response = await fetch('/apps/file-upload', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('File upload failed');
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('File upload error:', error);

      // Fallback to data URL even for large files
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }
  }

  /**
   * Validate required text/file fields for selected options & standalone blocks.
   * Returns true if all required fields are satisfied, false otherwise.
   */
  validateRequiredFields() {
    let firstInvalid = null;

    // Helper: mark a field container as invalid
    const setFieldError = (container, message) => {
      if (!container) return;
      container.classList.add('rf-field--invalid');
      let errorEl = container.querySelector('.rf-field-required-error');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'rf-field-required-error';
        container.appendChild(errorEl);
      }
      errorEl.textContent = message;
      errorEl.style.display = 'block';
      if (!firstInvalid) firstInvalid = container;
    };

    // Helper: clear invalid state
    const clearFieldError = (container) => {
      if (!container) return;
      container.classList.remove('rf-field--invalid');
      const errorEl = container.querySelector('.rf-field-required-error');
      if (errorEl) errorEl.style.display = 'none';
    };

    // ── A: rf-position-option text & file fields (only when option is checked) ──
    const checkedOptions = document.querySelectorAll('.rf-option-input:checked');
    checkedOptions.forEach(input => {
      // Skip if option is not visible for current method
      const block = input.closest('.rf-position-option');
      if (!block) return;
      const blockMethod = block.dataset.method;
      if (blockMethod && blockMethod !== 'all' && blockMethod !== this.currentMethod) return;

      const blockId = input.id?.replace('rf-option-', '');
      if (!blockId) return;

      // Check required text field
      if (input.dataset.hasTextInput === 'true') {
        const textField = document.querySelector('.rf-option-text-field[data-rf-option-text="' + blockId + '"]');
        if (textField && textField.hasAttribute('required')) {
          const textContainer = textField.closest('.rf-option-text-input');
          if (!textField.value || textField.value.trim() === '') {
            setFieldError(textContainer, 'This field is required.');
          } else {
            clearFieldError(textContainer);
          }
        }
      }

      // Check required file upload
      if (input.dataset.hasFileUpload === 'true') {
        const fileInput = document.querySelector('.rf-option-file-input[data-rf-option-file="' + blockId + '"]');
        if (fileInput && fileInput.hasAttribute('required')) {
          const fileContainer = fileInput.closest('.rf-option-file-upload');
          const hasFiles = window.rfOptionFiles && window.rfOptionFiles[blockId] && window.rfOptionFiles[blockId].length > 0;
          if (!hasFiles) {
            setFieldError(fileContainer, 'Please upload at least one file.');
          } else {
            clearFieldError(fileContainer);
          }
        }
      }
    });

    // ── B: Standalone rf-text-input blocks (visible & required) ──
    const standaloneTextInputs = document.querySelectorAll('[data-rf-input="text"][required]');
    standaloneTextInputs.forEach(input => {
      const block = input.closest('.rf-text-input');
      if (!block) return;
      const blockMethod = block.dataset.method;
      if (blockMethod && blockMethod !== 'all' && blockMethod !== this.currentMethod) return;
      // Also skip if hidden via display:none
      if (block.style.display === 'none') return;

      if (!input.value || input.value.trim() === '') {
        setFieldError(block, 'This field is required.');
      } else {
        clearFieldError(block);
      }
    });

    // ── C: Standalone rf-file-upload blocks (visible & required) ──
    const standaloneUploads = document.querySelectorAll('.rf-file-upload');
    standaloneUploads.forEach(container => {
      const fileInput = container.querySelector('[data-rf-file-input]');
      if (!fileInput || !fileInput.hasAttribute('required')) return;
      const blockMethod = container.dataset.method;
      if (blockMethod && blockMethod !== 'all' && blockMethod !== this.currentMethod) return;
      if (container.style.display === 'none') return;

      // Check if files exist in preview (rf-file-item elements)
      const previewItems = container.querySelectorAll('.rf-file-item');
      if (previewItems.length === 0) {
        setFieldError(container, 'Please upload at least one file.');
      } else {
        clearFieldError(container);
      }
    });

    // Scroll to first invalid field
    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    return true;
  }

  /**
   * Handle add to cart with RF customization
   */
  async handleRFAddToCart(form) {
    if (!this.validateRequiredOptionGroups()) {
      return;
    }

    if (!this.validateRequiredFields()) {
      return;
    }

    const submitButton = form.querySelector('[type="submit"]');
    submitButton.setAttribute('disabled', true);
    submitButton.classList.add('loading');

    try {
      // Ensure selectedOptions is fresh before processing
      this.updateSelectedOptions();

      // Use FormData to handle files properly (like product-form.js)
      const formData = new FormData(form);

      // Main product
      const mainVariantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity')) || 1;

      // Add selected options as properties
      const selectedOptions = document.querySelectorAll('.rf-option-input:checked');
      const optionValues = [];
      let totalOptionPrice = 0;
      let hasSelectedOptionWithAdditionalFields = false;

      const mainProductSection = document.querySelector('.main-product-page');
      const plainCartTitleSetting = mainProductSection?.dataset.rfPlainCartTitle?.trim() || 'Customisation Details';
      const plainSelectedLabelSetting = mainProductSection?.dataset.rfPlainSelectedLabel?.trim() || 'Selected Options';

      selectedOptions.forEach(input => {
        const block = input.closest('[data-method]');
        const blockMethod = block?.dataset.method;

        // Only include visible options
        if (!block || blockMethod === 'all' || blockMethod === this.currentMethod) {
          // Skip one-time fee options — they have their own separate line item in cart
          const isOneTimeFee = input.dataset.oneTimeFee === 'true';
          if (isOneTimeFee) return;

          const hasTextInput = input.dataset.hasTextInput === 'true';
          const hasFileUpload = input.dataset.hasFileUpload === 'true';
          if (hasTextInput || hasFileUpload) {
            hasSelectedOptionWithAdditionalFields = true;
          }

          // Track option products for price calculation
          const productPrice = parseFloat(input.dataset.productPrice) || 0;
          totalOptionPrice += productPrice;

          // Include price in display value
          if (productPrice > 0) {
            const formattedPrice = (productPrice / 100).toFixed(2);
            const cs = window.Shopify?.currency?.active === 'EUR' ? '€' : (window.Shopify?.currency?.active === 'USD' ? '$' : '£');
            optionValues.push(`${input.value} (+${cs}${formattedPrice})`);
          } else {
            optionValues.push(input.value);
          }
        }
      });

      if (optionValues.length > 0) {
        const isPlainModeSelection = !hasSelectedOptionWithAdditionalFields;
        const selectedOptionsLabel = isPlainModeSelection ? plainSelectedLabelSetting : 'Selected Options';

        formData.append(`properties[${selectedOptionsLabel}]`, optionValues.join(', '));

        if (isPlainModeSelection) {
          formData.append('properties[_rf_ui_cart_title]', plainCartTitleSetting);
          formData.append('properties[_rf_ui_selected_label]', selectedOptionsLabel);
        }

        // Add total options price as hidden property for reference
        if (totalOptionPrice > 0) {
          formData.append('properties[_Options Total Price]', `${(totalOptionPrice * quantity / 100).toFixed(2)} ${window.Shopify.currency.active}`);
        }
      }

      // Collect text from option text fields (within rf-position-option)
      const optionTextFields = document.querySelectorAll('.rf-option-text-field');
      optionTextFields.forEach(textInput => {
        const blockId = textInput.dataset.rfOptionText;
        const optionInput = document.getElementById('rf-option-' + blockId);

        // Only include if option is checked and field has value
        if (optionInput && optionInput.checked && textInput.value && textInput.value.trim() !== '') {
          const block = textInput.closest('.rf-position-option');
          const blockMethod = block?.dataset.method;

          // Only include visible options
          if (!block || blockMethod === 'all' || blockMethod === this.currentMethod) {
            const labelElement = textInput.previousElementSibling;
            const label = labelElement ? labelElement.textContent.trim() : 'Custom Text';
            const optionName = optionInput.value;
            formData.append(`properties[${optionName} - ${label}]`, textInput.value);
          }
        }
      });

      // Collect files from option file uploads (within rf-position-option)
      if (window.rfOptionFiles) {
        for (const blockId in window.rfOptionFiles) {
          const optionInput = document.getElementById('rf-option-' + blockId);
          const files = window.rfOptionFiles[blockId];

          // Only include if option is checked and has files
          if (optionInput && optionInput.checked && files && files.length > 0) {
            const block = optionInput.closest('.rf-position-option');
            const blockMethod = block?.dataset.method;

            // Only include visible options
            if (!block || blockMethod === 'all' || blockMethod === this.currentMethod) {
              const optionName = optionInput.value;

              // Append files directly to FormData (Shopify will upload automatically)
              for (let index = 0; index < files.length; index++) {
                const file = files[index];
                formData.append(`properties[${optionName} - File ${index + 1}]`, file);
              }
            }
          }
        }
      }

      // Collect all visible standalone text inputs
      const textInputs = document.querySelectorAll('[data-rf-input="text"]');
      textInputs.forEach(input => {
        const block = input.closest('[data-method]');
        const blockMethod = block?.dataset.method;

        // Only include visible text inputs
        if (input.value && input.value.trim() !== '' && (!block || blockMethod === 'all' || blockMethod === this.currentMethod)) {
          const label = input.id.replace('rf-text-', '');
          const labelElement = document.querySelector(`label[for="${input.id}"]`);
          const fieldName = labelElement ? labelElement.textContent.replace('*', '').trim() : `Text ${label}`;
          formData.append(`properties[${fieldName}]`, input.value);
        }
      });

      // Add standalone file uploads to properties
      const fileUploads = document.querySelectorAll('.rf-file-upload');
      let fileIndex = 1;

      for (const uploadContainer of fileUploads) {
        const blockMethod = uploadContainer.dataset.method;

        // Only process visible upload containers
        if (!blockMethod || blockMethod === 'all' || blockMethod === this.currentMethod) {
          const blockId = uploadContainer.dataset.blockId;
          const filesArray = this.uploadedFiles.filter(f => {
            // Find files belonging to this container
            const fileItems = uploadContainer.querySelectorAll('.rf-file-item');
            return Array.from(fileItems).some(item =>
              parseInt(item.dataset.fileIndex) === f.index
            );
          });

          // Append files directly to FormData (Shopify will upload automatically)
          for (const fileObj of filesArray) {
            const file = fileObj.file;

            // Get the label for this upload block
            const labelElement = uploadContainer.querySelector('.rf-upload-label');
            const label = labelElement ? labelElement.textContent.replace('*', '').trim() : 'Uploaded File';

            formData.append(`properties[${label} ${fileIndex}]`, file);
            fileIndex++;
          }
        }
      }

      // Add option product info as properties (for display only)
      // Only include non-fee options in _Option Products
      const nonFeeOptions = this.selectedOptions.filter(opt => !opt.isOneTimeFee);
      if (nonFeeOptions.length > 0) {
        const optionDetails = nonFeeOptions.map(opt => `${opt.title} (+£${(opt.price/100).toFixed(2)})`);
        formData.append('properties[_Option Products]', optionDetails.join(', '));
      }

      const cartNotification = document.querySelector('cart-notification');

      // PRE-ADD: Check cart for existing same variant and merge quantities
      // ONLY merge if current add has NO customization (no files, no text, no options)
      let mergeExistingQty = 0;
      let existingItemKeysToRemove = [];
      let existingOptionKeysToRemove = [];

      // Detect if this add has any customization (files, text inputs, selected options)
      const hasFiles = (window.rfOptionFiles && Object.keys(window.rfOptionFiles).some(k => window.rfOptionFiles[k]?.length > 0)) ||
                       (this.uploadedFiles && this.uploadedFiles.length > 0);
      const hasOptionText = Array.from(document.querySelectorAll('.rf-option-text-field')).some(f => {
        const optInput = document.getElementById('rf-option-' + f.dataset.rfOptionText);
        return optInput && optInput.checked && f.value && f.value.trim() !== '';
      });
      const hasStandaloneText = Array.from(document.querySelectorAll('[data-rf-input="text"]')).some(f => f.value && f.value.trim() !== '');
      const hasCustomization = hasFiles || hasOptionText || hasStandaloneText;

      try {
        const preCartResponse = await fetch('/cart.js');
        const preCartData = await preCartResponse.json();

        // Find existing main items (not options, not fees) with same variant_id
        for (const item of preCartData.items) {
          // Skip option items
          if (item.properties?.['_rf_parent']) continue;
          // Skip one-time fee items
          if (item.properties?.['_rf_one_time_fee'] === 'true') continue;

          if (String(item.variant_id) === String(mainVariantId)) {
            // Check if existing item also has customization
            const selectedLabelKey = item.properties?._rf_ui_selected_label || 'Selected Options';
            const existingHasCustomization = item.properties && Object.keys(item.properties).some(key =>
              !key.startsWith('_') && key !== 'Selected Options' && key !== selectedLabelKey && key !== 'Customization Method'
            );

            // Only merge if NEITHER has customization (plain items)
            if (!hasCustomization && !existingHasCustomization) {
              mergeExistingQty += item.quantity;
              existingItemKeysToRemove.push(item.key);

              // Also find option items linked to this existing item
              for (const optItem of preCartData.items) {
                if (optItem.properties?.['_rf_parent'] === String(item.variant_id) ||
                    optItem.properties?.['_rf_parent_key'] === item.key) {
                  existingOptionKeysToRemove.push(optItem.key);
                }
              }
            }
          }
        }

        // Remove existing items + their options before adding merged version
        if (existingItemKeysToRemove.length > 0) {
          const allKeysToRemove = [...existingItemKeysToRemove, ...existingOptionKeysToRemove];
          const removeUpdates = {};
          allKeysToRemove.forEach(key => removeUpdates[key] = 0);
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: removeUpdates })
          });
        }
      } catch (e) {
        // Continue anyway — worst case we get a duplicate (non-critical)
      }

      // Calculate final quantity (existing + new)
      const finalQuantity = mergeExistingQty + quantity;
      // console.log('[RF Phase1] variant:', mainVariantId, '| qty from form:', quantity, '| mergeExistingQty:', mergeExistingQty, '| finalQuantity:', finalQuantity, '| hasCustomization:', hasCustomization);

      // Keep original FormData aligned with merged quantity
      // (important for background file-upload phase which reuses this FormData)
      formData.set('quantity', String(finalQuantity));

      // Update hidden total price property if present
      if (formData.has('properties[_Options Total Price]') && totalOptionPrice > 0) {
        formData.set('properties[_Options Total Price]', `${(totalOptionPrice * finalQuantity / 100).toFixed(2)} ${window.Shopify.currency.active}`);
      }

      // PHASE 1 (FAST): Add product WITHOUT files BUT with upload placeholders

      // Collect files that need to be uploaded later
      const filesToUpload = [];
      const formDataWithPlaceholders = new FormData(form);

      // Copy all custom properties from formData to formDataWithPlaceholders
      // (formData has properties added by RF like _Option Products, Selected Options, etc.)
      let fileCount = 0;
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          filesToUpload.push({ key, file: value });
          fileCount++;
          // Replace file with placeholder text
          const propertyName = key.match(/properties\[(.*?)\]/)?.[1] || 'File';
          formDataWithPlaceholders.set(key, `🔄 Uploading ${value.name}...`);
        } else if (key.startsWith('properties[') && !formDataWithPlaceholders.has(key)) {
          // Copy non-file properties that aren't already in the form
          formDataWithPlaceholders.append(key, value);
        }
      }

      // Override quantity with merged quantity
      if (mergeExistingQty > 0) {
        formDataWithPlaceholders.set('quantity', String(finalQuantity));
      }

      // Add upload status property
      if (fileCount > 0) {
        formDataWithPlaceholders.append('properties[_Upload Status]', `⏳ Uploading ${fileCount} file${fileCount > 1 ? 's' : ''}...`);
      }

      // Step 1: Add main product with placeholders (FAST - 200ms)
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          "Accept": "application/javascript"
        },
        body: formDataWithPlaceholders
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.description || 'Failed to add to cart');
      }

      const addResult = await response.json();
      const mainLineItemKey = addResult.key; // Store line item key for later file upload
      // console.log('[RF Phase1] Item added:', mainLineItemKey, '| qty in response:', addResult.quantity);

      // Step 2: Add option products with _rf_parent linking (FAST - 500ms)
      if (this.selectedOptions.length > 0) {
        // Get the main product ID (not variant) for one-time fee tracking
        const mainProductId = this.getMainProductId();

        // Check cart for existing one-time fees for this product
        let existingOneTimeFees = [];
        try {
          const cartCheckResponse = await fetch('/cart.js');
          const cartData = await cartCheckResponse.json();
          existingOneTimeFees = cartData.items
            .filter(item => 
              item.properties?.['_rf_one_time_fee'] === 'true' &&
              item.properties?.['_rf_main_product_id'] === mainProductId
            )
            .map(item => item.variant_id);
        } catch (e) {
          // Continue with empty existing fees list if cart check fails
        }
        
        const optionItems = this.selectedOptions
          .filter(option => {
            // Skip one-time fee if already exists in cart for this product
            if (option.isOneTimeFee && existingOneTimeFees.includes(parseInt(option.id))) {
              return false;
            }
            return true;
          })
          .map(option => {
            // One-time fee: quantity is always 1, regardless of main product quantity
            const optionQuantity = option.isOneTimeFee ? 1 : finalQuantity;
            
            // One-time fee options are VISIBLE in cart (no _rf_parent)
            // Regular options are HIDDEN in cart (have _rf_parent)
            if (option.isOneTimeFee) {
              const feeProps = {
                '_rf_one_time_fee': 'true',
                '_rf_main_product_id': mainProductId || '',
                '_rf_fee_cart_title': option.oneTimeFeeCartTitle || option.value
              };
              if (option.isPreSelected) {
                feeProps['_rf_pre_selected'] = 'true';
              }
              return {
                id: option.id,
                quantity: optionQuantity,
                properties: feeProps
              };
            } else {
              return {
                id: option.id,
                quantity: optionQuantity,
                properties: {
                  '_rf_parent': String(mainVariantId),
                  '_rf_parent_key': mainLineItemKey,
                  '_rf_option_title': option.value || option.title,
                  '_rf_one_time_fee': 'false',
                  '_rf_main_product_id': mainProductId || ''
                }
              };
            }
          });
        
        if (optionItems.length > 0) {
          const optionsResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items: optionItems })
          });
          // Wait for response to complete before continuing
          await optionsResponse.json();
        }

        // Store option product IDs in localStorage for cart sync
        try {
          const existingSync = JSON.parse(localStorage.getItem('rf_cart_sync') || '{}');
          if (!existingSync[mainVariantId]) {
            existingSync[mainVariantId] = [];
          }
          this.selectedOptions.forEach(option => {
            if (!existingSync[mainVariantId].includes(option.id)) {
              existingSync[mainVariantId].push(option.id);
            }
          });
          localStorage.setItem('rf_cart_sync', JSON.stringify(existingSync));
        } catch(e) {
          // Ignore localStorage failures (private mode, quota, etc.)
        }
      }

      // Step 3: Show drawer with all products including options

      // Small delay to ensure Shopify has processed all cart additions
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (cartNotification) {
        const sections = cartNotification.getSectionsToRender().map((section) => section.id).join(',');
        const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sections}`);
        const sectionsData = await sectionsResponse.json();

        cartNotification.renderContents({ sections: sectionsData });
      } else if (document.body.classList.contains('minicart-disable')) {
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        if (window.cartCount) {
          window.cartCount(cartData);
        }
        if (window.setCartCountTab) {
          window.setCartCountTab();
        }
      }

      // Re-check one-time fee options so already-added fees become non-selectable
      await this.syncOneTimeFeeOptionAvailability();

      // PHASE 2 (SLOW): Upload files in background and update cart
      if (filesToUpload.length > 0) {
        // Add to upload queue instead of processing immediately
        this.uploadQueue.push({
          formData: formData,
          mainLineItemKey: mainLineItemKey,
          mainVariantId: mainVariantId,
          cartNotification: cartNotification
        });
        
        // Start processing queue if not already running
        this.processUploadQueue();
      }

      // Clear form
      this.resetCustomization();

    } catch (error) {
      console.error('Add to cart error:', error);
      const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
      const errorMessage = form.querySelector('.product-form__error-message');

      if (errorWrapper && errorMessage) {
        errorWrapper.classList.remove('d-none');
        errorMessage.textContent = error.message;
      } else {
        alert(error.message);
      }
    } finally {
      submitButton.classList.remove('loading');
      submitButton.removeAttribute('disabled');
    }
  }

  /**
   * Process upload queue one by one
   */
  async processUploadQueue() {
    // If already processing, don't start another
    if (this.isProcessingQueue) return;
    
    // If queue is empty, nothing to do
    if (this.uploadQueue.length === 0) return;
    
    this.isProcessingQueue = true;
    this.isUploadingFiles = true;
    window.RF_FILE_UPLOADING = true;
    this.blockCheckout(true);

    // Use XMLHttpRequest directly to bypass ALL fetch monkey-patches.
    // shop_events_listener (Shopify pixels) patches window.fetch BEFORE rf-cart-sync loads,
    // so even window.RF_CART_SYNC.originalFetch is already the patched version.
    // XHR is not patched by any script — it's the only truly clean HTTP method available.
    const rawFetch = (url, options = {}) => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url);
      if (options.headers) {
        Object.entries(options.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      }
      const respond = () => resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => {
          try { return Promise.resolve(JSON.parse(xhr.responseText)); }
          catch(e) { return Promise.resolve({}); }
        },
        text: () => Promise.resolve(xhr.responseText || '')
      });
      xhr.onload = respond;
      // onerror also resolves (not rejects) so retry logic can run
      xhr.onerror = respond;
      xhr.send(options.body || null);
    });
    
    while (this.uploadQueue.length > 0) {
      const task = this.uploadQueue.shift();

      try {
        // Re-add product WITH real files (this will upload them to Shopify)
        const formDataWithFiles = new FormData();

        // Copy all original form data including files
        for (const [key, value] of task.formData.entries()) {
          formDataWithFiles.append(key, value);
        }

        // Remove the placeholder upload status
        formDataWithFiles.delete('properties[_Upload Status]');

        // Add with correct final quantity directly.
        // Previously we added qty=1 then updated, but Shopify re-keys items when
        // automatic discounts apply (at 10+ qty threshold), causing the update to fail.
        // Adding with the correct qty in one shot avoids the re-key issue entirely.
        const originalQty = formDataWithFiles.get('quantity');
        // Keep quantity as-is (already set to finalQuantity by Phase 1)

        // console.log('[RF Phase2] START placeholder:', task.mainLineItemKey, '| rawFetch:', typeof rawFetch, '| target qty:', originalQty);

        // Add product with REAL files (qty=1 to avoid duplication)
        const uploadResponse = await rawFetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { "Accept": "application/javascript" },
          body: formDataWithFiles
        });

        if (!uploadResponse.ok) {
          const errText = await uploadResponse.text();
          console.error('[RF Phase2] Add failed:', uploadResponse.status, errText);
          throw new Error('File upload failed');
        }

        const uploadResult = await uploadResponse.json();
        const newMainLineItemKey = uploadResult.key;
        const newMainVariantId = uploadResult.variant_id;
        // console.log('[RF Phase2] Real item added:', newMainLineItemKey, '| variant:', newMainVariantId);

        // Remove placeholder — if 400 (re-keyed by discount), find new key via cart
        // console.log('[RF Phase2] Removing placeholder:', task.mainLineItemKey);
        let placeholderKeyToRemove = task.mainLineItemKey;
        const removeResp = await rawFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: placeholderKeyToRemove, quantity: 0 })
        });
        // console.log('[RF Phase2] Placeholder remove status:', removeResp.status);

        // If 400 — placeholder was re-keyed by Shopify discount, find it by _Upload Status property
        if (removeResp.status === 400) {
          const searchCart = await rawFetch('/cart.js');
          const searchData = await searchCart.json();
          const reKeyedPlaceholder = searchData.items.find(i =>
            String(i.variant_id) === String(newMainVariantId) &&
            i.properties?.['_Upload Status'] &&
            !i.properties?.['_rf_parent']
          );
          if (reKeyedPlaceholder) {
            placeholderKeyToRemove = reKeyedPlaceholder.key;
            await rawFetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: placeholderKeyToRemove, quantity: 0 })
            });
          }
        }

        // Update new item to the correct final quantity using /cart/update.js
        // IMPORTANT: We use update.js (not change.js) because change.js re-triggers
        // Shopify's discount engine on every call, causing infinite re-keying.
        // update.js accepts { updates: { key: qty } } and is more stable.
        let finalMainKey = newMainLineItemKey;

        if (parseInt(originalQty) > 1) {
          // First fetch fresh cart to find the actual current key (may have been re-keyed)
          const freshCart = await rawFetch('/cart.js');
          const freshData = await freshCart.json();

          // Find the real item by variant_id — not a placeholder, not an option, not a fee
          const realItem = freshData.items.find(i =>
            String(i.variant_id) === String(newMainVariantId) &&
            !i.properties?.['_rf_parent'] &&
            i.properties?.['_rf_one_time_fee'] !== 'true' &&
            !i.properties?.['_Upload Status']
          );

          if (realItem) {
            finalMainKey = realItem.key;
            // console.log('[RF Phase2] Real item found:', finalMainKey, '| current qty:', realItem.quantity, '| target:', originalQty);

            if (realItem.quantity !== parseInt(originalQty)) {
              // Use update.js with key-based update — does not re-trigger discount engine
              const updateResp = await rawFetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: { [finalMainKey]: parseInt(originalQty) } })
              });
              // console.log('[RF Phase2] update.js status:', updateResp.status);
            } else {
              // console.log('[RF Phase2] qty already correct, no update needed');
            }
          } else {
            // console.log('[RF Phase2] WARNING: real item not found for variant', newMainVariantId);
          }
        }

        // Find option items linked to the placeholder and re-add them with correct parent key
        // Get fresh cart to find current state of real item (may have been re-keyed again)
        const cartResponse2 = await rawFetch('/cart.js');
        const cartData2 = await cartResponse2.json();

        // Find the definitive current key of the real item.
        // Strategy 1: exact key match (best case — no re-keying happened)
        // Strategy 2: match by finalMainKey (set after Phase 2 add)
        // Strategy 3: match by variant_id ONLY if it's the unique main item for that variant
        let definitiveMainKey = finalMainKey;
        const exactKeyItem = cartData2.items.find(i => i.key === finalMainKey);
        if (exactKeyItem) {
          definitiveMainKey = finalMainKey;
        } else {
          // Count main items with this variant_id
          const mainItemsForVariant = cartData2.items.filter(i =>
            String(i.variant_id) === String(newMainVariantId) &&
            !i.properties?.['_rf_parent'] &&
            i.properties?.['_rf_one_time_fee'] !== 'true' &&
            !i.properties?.['_Upload Status']
          );
          if (mainItemsForVariant.length === 1) {
            // Safe — only one main item for this variant
            definitiveMainKey = mainItemsForVariant[0].key;
          } else if (mainItemsForVariant.length > 1) {
            // Multiple main items — find by matching the variant part of original key
            const originalVariantPart = task.mainLineItemKey.split(':')[0];
            const matched = mainItemsForVariant.find(i => i.key.startsWith(originalVariantPart + ':'));
            definitiveMainKey = matched ? matched.key : finalMainKey;
          }
        }
        // console.log('[RF Phase2] Definitive main key for option re-link:', definitiveMainKey);

        const optionsToReAdd = [];
        const optionKeysToRemove = [];
        
        for (const item of cartData2.items) {
          if (item.properties && item.properties['_rf_parent_key'] === task.mainLineItemKey) {
            optionsToReAdd.push({
              id: item.variant_id,
              quantity: item.quantity,
              properties: {
                '_rf_parent': String(newMainVariantId),
                '_rf_parent_key': definitiveMainKey,
                '_rf_option_title': item.properties['_rf_option_title'],
                '_rf_one_time_fee': item.properties['_rf_one_time_fee'] || 'false',
                '_rf_main_product_id': item.properties['_rf_main_product_id'] || ''
              }
            });
            optionKeysToRemove.push(item.key);
          }
        }

        // Remove old options (with wrong parent key)
        if (optionKeysToRemove.length > 0) {
          const removeUpdates = {};
          optionKeysToRemove.forEach(key => removeUpdates[key] = 0);
          await rawFetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: removeUpdates })
          });
        }

        // Add options back with correct parent key
        if (optionsToReAdd.length > 0) {
          await rawFetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: optionsToReAdd })
          });
        }

      } catch (error) {
        console.error('Background file upload failed for', task.mainLineItemKey, error);
      }
    }
    
    // All uploads complete
    this.isProcessingQueue = false;
    this.isUploadingFiles = false;
    window.RF_FILE_UPLOADING = false;
    this.blockCheckout(false);

    // Fix any broken parent keys caused by Shopify discount re-keying during Phase 2
    // RF_FILE_UPLOADING is now false so cart-sync will process normally going forward
    if (window.RF_CART_SYNC) {
      try {
        await window.RF_CART_SYNC.loadCartWithFetch(window.RF_CART_SYNC.originalFetch);
        await window.RF_CART_SYNC.fixBrokenParentKeys(window.RF_CART_SYNC.originalFetch);
      } catch(e) {}
    }

    // Refresh cart to show uploaded files
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification) {
      try {
        const sections = cartNotification.getSectionsToRender().map((section) => section.id).join(',');
        const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sections}`);
        const sectionsData = await sectionsResponse.json();
        cartNotification.renderContents({ sections: sectionsData });
      } catch (e) {
        console.error('Failed to refresh cart:', e);
      }
    }
  }

  /**
   * Block/unblock checkout button while files are uploading
   */
  blockCheckout(block) {
    // Find all checkout buttons in cart drawer and cart page
    const checkoutButtons = document.querySelectorAll('[name="checkout"], [href*="/checkout"], button[type="submit"][form*="cart"]');

    checkoutButtons.forEach(button => {
      if (block) {
        button.setAttribute('disabled', true);
        button.dataset.rfBlocked = 'true';

        // Add visual indicator
        const originalText = button.textContent;
        button.dataset.originalText = originalText;
        button.textContent = '⏳ Uploading files...';
      } else {
        button.removeAttribute('disabled');
        button.removeAttribute('data-rf-blocked');

        // Restore original text
        if (button.dataset.originalText) {
          button.textContent = button.dataset.originalText;
          button.removeAttribute('data-original-text');
        }
      }
    });
  }

  /**
   * Reset customization after successful add to cart
   */
  resetCustomization() {
    // Uncheck all options (skip pre-selected)
    document.querySelectorAll('.rf-option-input:checked').forEach(input => {
      if (input.dataset.preSelected === 'true') return;
      input.checked = false;
      input.dispatchEvent(new Event('change'));
    });

    // Close all active option cards and hide additional fields
    document.querySelectorAll('.rf-option-card-wrapper.rf-active').forEach(wrapper => {
      wrapper.classList.remove('rf-active');
    });
    document.querySelectorAll('.rf-option-additional-fields').forEach(fields => {
      fields.style.display = 'none';
    });

    // Clear standalone text inputs
    document.querySelectorAll('[data-rf-input="text"]').forEach(input => {
      input.value = '';
    });

    // Clear option text fields
    document.querySelectorAll('.rf-option-text-field').forEach(input => {
      input.value = '';
    });

    // Reset character counters
    document.querySelectorAll('.rf-option-char-count').forEach(counter => {
      counter.textContent = '0';
    });

    // Clear standalone file uploads
    document.querySelectorAll('.rf-files-preview').forEach(preview => {
      preview.innerHTML = '';
    });

    // Clear option file uploads
    document.querySelectorAll('.rf-option-files-preview').forEach(preview => {
      preview.innerHTML = '';
    });

    // Reset file inputs
    document.querySelectorAll('.rf-option-file-input').forEach(input => {
      input.value = '';
    });

    // Clear option files from global storage
    if (window.rfOptionFiles) {
      window.rfOptionFiles = {};
    }

    this.selectedOptions = [];
    this.uploadedFiles = [];

    // Reset quantity input back to 1
    const qtyInput = document.querySelector('product-form form input[name="quantity"]');
    if (qtyInput) {
      qtyInput.value = '1';
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new RFCustomizer();
  });
} else {
  new RFCustomizer();
}
